/**
 * Cart Drawer — Premium slide-out cart with progress bar, upsell carousel,
 * trust badges, Klarna messaging, and payment method indicators.
 * Custom element <cart-drawer>
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // DOM refs
    this.overlay           = this.querySelector('[data-cart-drawer-overlay]');
    this.drawer            = this.querySelector('[data-cart-drawer]');
    this.itemsContainer    = this.querySelector('[data-cart-drawer-items]');
    this.emptyContainer    = this.querySelector('[data-cart-empty]');
    this.subtotalEl        = this.querySelector('[data-cart-drawer-subtotal]');
    this.countEl           = this.querySelector('[data-cart-drawer-count]');
    this.progressRoot      = this.querySelector('[data-cart-progress]');
    this.progressFill      = this.querySelector('[data-cart-progress-fill]');
    this.progressMsg       = this.querySelector('[data-cart-progress-message]');
    this.upsellSection     = this.querySelector('[data-cart-upsell]');
    this.upsellContainer   = this.querySelector('[data-cart-recommendations]');
    this.klarnaMsg         = this.querySelector('[data-cart-klarna]');
    this.klarnaAmount      = this.querySelector('[data-klarna-installment]');
    this.upsellUrlTemplate = this.upsellContainer ? this.upsellContainer.dataset.urlTemplate : '';

    // Config (from Liquid)
    this.threshold   = parseInt(this.dataset.freeShippingThreshold, 10) || 5000; // cents
    this.showUpsell  = this.dataset.showUpsell === 'true';
    this.showKlarna  = this.dataset.showKlarna === 'true';

    // Translations (from Liquid data-i18n-* attrs; Dutch fallbacks match the default locale)
    this.t = {
      progressInitial:    this.dataset.i18nProgressInitial      || 'Voeg artikelen toe om gratis verzending te ontgrendelen',
      progressUnlocked:   this.dataset.i18nProgressUnlocked     || '🎉 Je hebt <strong>gratis verzending</strong> ontgrendeld',
      progressRemaining:  this.dataset.i18nProgressRemaining    || 'Voeg <strong>__AMOUNT__</strong> toe voor <strong>gratis verzending</strong>',
      unlocksFreeDelivery:this.dataset.i18nUnlocksFreeDelivery  || 'Ontgrendelt gratis verzending',
      fillerHint:         this.dataset.i18nFillerHint           || ' — voeg er een toe hieronder 👇',
      removeItem:         this.dataset.i18nRemoveItem           || '__TITLE__ verwijderen',
      qtyDecrease:        this.dataset.i18nQtyDecrease          || 'Aantal verminderen',
      qtyIncrease:        this.dataset.i18nQtyIncrease          || 'Aantal verhogen'
    };

    // State
    this.lastAddedProductId = null;
    this.isLoadingRecs      = false;
    this._lastSubtotal      = null;

    this.bindEvents();
  }

  // ══════════════════════════════════════════
  // EVENTS
  // ══════════════════════════════════════════

  bindEvents() {
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    const closeBtn = this.querySelector('[data-cart-drawer-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-cart-drawer-toggle]');
      if (toggle) {
        e.preventDefault();
        this.isOpen() ? this.close() : this.open();
      }
    });

    // Delegated clicks inside drawer
    this.addEventListener('click', (e) => {
      // Qty +/-
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const direction = qtyBtn.dataset.qtyChange;
        const current = parseInt(qtyBtn.parentElement.querySelector('input').value, 10) || 1;
        const next = direction === 'plus' ? current + 1 : Math.max(0, current - 1);
        this.updateQuantity(key, next, qtyBtn.closest('.cart-item'));
      }

      // Remove line item
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        const lineEl = removeBtn.closest('.cart-item');
        this.removeLineItem(removeBtn.dataset.removeItem, lineEl);
      }

      // Upsell +
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) {
        e.preventDefault();
        this.quickAdd(addBtn.dataset.variantId, addBtn);
      }
    });

    // Refresh on external add events (from product form)
    document.body.addEventListener('cart:add', (e) => {
      if (e.detail && e.detail.item) {
        this.lastAddedProductId = e.detail.item.product_id;
      }
      this.refresh();
    });
  }

  // ══════════════════════════════════════════
  // DRAWER OPEN / CLOSE
  // ══════════════════════════════════════════

  isOpen() {
    return this.drawer && this.drawer.classList.contains('is-open');
  }

  async open() {
    await this.refresh();
    if (this.overlay) this.overlay.classList.add('is-open');
    if (this.drawer)  this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.trapFocus();
  }

  close() {
    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this.drawer)  this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    this.releaseFocus();
  }

  trapFocus() {
    this._previouslyFocused = document.activeElement;
    this._trapHandler = (e) => {
      if (e.key !== 'Tab' || !this.drawer) return;
      const focusables = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this._trapHandler);
    const closeBtn = this.querySelector('[data-cart-drawer-close]');
    if (closeBtn) closeBtn.focus();
  }

  releaseFocus() {
    if (this._trapHandler) {
      document.removeEventListener('keydown', this._trapHandler);
      this._trapHandler = null;
    }
    if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
      this._previouslyFocused.focus();
    }
    this._previouslyFocused = null;
  }

  // ══════════════════════════════════════════
  // DATA FETCH
  // ══════════════════════════════════════════

  async refresh() {
    try {
      const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  async updateQuantity(key, quantity, lineEl) {
    try {
      if (quantity === 0 && lineEl) {
        // Visually collapse before the request resolves
        this.collapseLine(lineEl);
      }
      const res  = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
      this.dispatchCartUpdate(cart);
    } catch (err) {
      console.error('Cart update error:', err);
    }
  }

  removeLineItem(key, lineEl) {
    this.updateQuantity(key, 0, lineEl);
  }

  async quickAdd(variantId, btn) {
    if (!variantId) return;
    btn.classList.add('is-loading');
    btn.disabled = true;
    try {
      const res  = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 })
      });
      const item = await res.json();
      if (item && item.product_id) this.lastAddedProductId = item.product_id;
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1200);
      btn.disabled = false;
      await this.refresh();
    } catch (err) {
      console.error('Quick-add error:', err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  // ══════════════════════════════════════════
  // RENDER ORCHESTRATION
  // ══════════════════════════════════════════

  renderCart(cart) {
    this.renderItems(cart);
    this.renderProgressBar(cart);
    this.renderSubtotal(cart);
    this.renderKlarna(cart);
    this.updateCartCount(cart.item_count);

    if (this.showUpsell) {
      this.loadRecommendations(cart);
    }
  }

  // ─── items ─────────────────────────────────

  renderItems(cart) {
    if (!this.itemsContainer) return;
    const isEmpty = cart.item_count === 0;

    // Empty state toggle
    if (this.emptyContainer) {
      this.emptyContainer.hidden = !isEmpty;
    }
    this.itemsContainer.hidden = isEmpty;

    if (isEmpty) {
      this.itemsContainer.innerHTML = '';
      return;
    }

    this.itemsContainer.innerHTML = cart.items.map(item => this.itemTemplate(item)).join('');
  }

  itemTemplate(item) {
    const imgSrc = item.image ? this.getSizedImageUrl(item.image, '200x') : '';
    const safeTitle = this.escapeHtml(item.product_title);
    const removeLabel = this.t.removeItem.replace('__TITLE__', safeTitle);
    const img = imgSrc
      ? `<img src="${this.escapeHtml(imgSrc)}" alt="${safeTitle}" loading="lazy" width="72" height="72">`
      : '';
    return `
      <div class="cart-item" data-line-key="${this.escapeHtml(item.key)}">
        <a href="${this.escapeHtml(item.url)}" class="cart-item__media">${img}</a>
        <div class="cart-item__info">
          <a href="${this.escapeHtml(item.url)}" class="cart-item__title">${safeTitle}</a>
          ${item.variant_title ? `<p class="cart-item__variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
          <div class="cart-item__row">
            <div class="qty-selector" role="group" aria-label="${this.escapeHtml(this.t.qtyDecrease)}">
              <button type="button" data-qty-change="minus" data-key="${this.escapeHtml(item.key)}" aria-label="${this.escapeHtml(this.t.qtyDecrease)}">−</button>
              <input type="number" value="${item.quantity}" min="0" readonly aria-label="${this.escapeHtml(this.t.qtyDecrease)}">
              <button type="button" data-qty-change="plus" data-key="${this.escapeHtml(item.key)}" aria-label="${this.escapeHtml(this.t.qtyIncrease)}">+</button>
            </div>
            <button type="button" class="cart-item__remove" data-remove-item="${this.escapeHtml(item.key)}" aria-label="${removeLabel}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
        <p class="cart-item__line-price">${this.formatMoney(item.final_line_price)}</p>
      </div>
    `;
  }

  collapseLine(lineEl) {
    if (!lineEl) return;
    lineEl.style.maxHeight = lineEl.offsetHeight + 'px';
    requestAnimationFrame(() => {
      lineEl.classList.add('is-removing');
      lineEl.style.maxHeight = '0';
    });
  }

  // ─── progress bar ──────────────────────────

  renderProgressBar(cart) {
    if (!this.progressRoot) return;

    if (cart.item_count === 0) {
      this.progressRoot.hidden = true;
      return;
    }
    this.progressRoot.hidden = false;

    const remaining = Math.max(0, this.threshold - cart.total_price);
    const pct       = Math.min(100, (cart.total_price / this.threshold) * 100);
    this._remainingForFreeShipping = remaining;

    if (this.progressFill) {
      this.progressFill.style.width = pct + '%';
      this.progressFill.classList.toggle('is-complete', pct >= 100);
    }
    this.progressRoot.classList.toggle('is-complete', pct >= 100);

    if (this.progressMsg) {
      this.progressMsg.innerHTML = remaining === 0
        ? this.t.progressUnlocked
        : this.t.progressRemaining.replace('__AMOUNT__', this.formatMoney(remaining));
    }
  }

  // Find the cheapest recommendation whose price crosses the threshold,
  // highlight it, and move it to the front of the carousel.
  highlightFreeShippingFiller() {
    if (!this.upsellContainer) return;
    const cards = Array.from(this.upsellContainer.querySelectorAll('[data-upsell-card]'));

    // Clear prior highlight
    cards.forEach(c => {
      c.classList.remove('is-free-shipping-filler');
      const tag = c.querySelector('.cart-upsell-card__filler-tag');
      if (tag) tag.remove();
    });

    const remaining = this._remainingForFreeShipping || 0;
    if (remaining <= 0 || cards.length === 0) return;

    // Eligible = price >= remaining (crosses threshold when added)
    const eligible = cards
      .map(card => ({ card, price: parseInt(card.dataset.priceCents, 10) || 0 }))
      .filter(x => x.price >= remaining)
      .sort((a, b) => a.price - b.price);

    if (eligible.length === 0) return;

    const winner = eligible[0].card;
    winner.classList.add('is-free-shipping-filler');

    // Add a small tag (SVG is static markup; label text is translated)
    const tag = document.createElement('span');
    tag.className = 'cart-upsell-card__filler-tag';
    const tagSvg = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;
    tag.innerHTML = tagSvg + ' ' + this.escapeHtml(this.t.unlocksFreeDelivery);
    winner.prepend(tag);

    // Move to first position
    if (winner.parentNode && winner.parentNode.firstElementChild !== winner) {
      winner.parentNode.insertBefore(winner, winner.parentNode.firstElementChild);
    }

    // Augment progress message with a subtle pointer
    if (this.progressMsg && !this.progressMsg.querySelector('.cart-progress__hint')) {
      const hint = document.createElement('span');
      hint.className = 'cart-progress__hint';
      hint.textContent = this.t.fillerHint;
      this.progressMsg.appendChild(hint);
    }
  }

  // ─── subtotal ──────────────────────────────

  renderSubtotal(cart) {
    if (!this.subtotalEl) return;
    const formatted = this.formatMoney(cart.total_price);
    if (this._lastSubtotal !== null && this._lastSubtotal !== cart.total_price) {
      this.subtotalEl.classList.remove('is-flash');
      void this.subtotalEl.offsetWidth;
      this.subtotalEl.classList.add('is-flash');
    }
    this.subtotalEl.textContent = formatted;
    this._lastSubtotal = cart.total_price;
  }

  // ─── klarna ────────────────────────────────

  renderKlarna(cart) {
    if (!this.klarnaMsg || !this.klarnaAmount) return;
    if (!this.showKlarna || cart.item_count === 0 || cart.total_price < 500) {
      this.klarnaMsg.hidden = true;
      return;
    }
    this.klarnaMsg.hidden = false;
    const perInstall = Math.round(cart.total_price / 3);
    this.klarnaAmount.textContent = this.formatMoney(perInstall);
  }

  // ─── header count pill ─────────────────────

  updateCartCount(count) {
    if (this.countEl) {
      this.countEl.textContent = count;
      this.countEl.classList.remove('is-bump');
      void this.countEl.offsetWidth;
      this.countEl.classList.add('is-bump');
    }
    // Also update site-header cart badge
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // ─── recommendations ───────────────────────

  async loadRecommendations(cart) {
    if (!this.upsellContainer || !this.upsellSection || !this.upsellUrlTemplate) return;
    if (this.isLoadingRecs) return;

    // Choose seed product
    let seedId = this.lastAddedProductId;
    if (!seedId && cart && cart.items && cart.items[0]) seedId = cart.items[0].product_id;

    // No seed → hide carousel
    if (!seedId) {
      this.upsellSection.hidden = true;
      this.upsellContainer.innerHTML = '';
      return;
    }

    // Cache hit — same seed, skip refetch but re-evaluate filler
    if (this.upsellContainer.dataset.loadedSeed === String(seedId)) {
      this.upsellSection.hidden = false;
      this.highlightFreeShippingFiller();
      return;
    }

    this.isLoadingRecs = true;
    const url = this.upsellUrlTemplate + encodeURIComponent(seedId);

    try {
      const res  = await fetch(url);
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('[data-cart-recs-inner]');

      if (inner && inner.children.length > 0) {
        // Move parsed nodes directly instead of stringifying — safer + faster.
        this.upsellContainer.replaceChildren(...Array.from(inner.children));
        this.upsellContainer.dataset.loadedSeed = seedId;
        this.upsellSection.hidden = false;
        // After injection, see if any card would unlock free delivery
        this.highlightFreeShippingFiller();
      } else {
        this.upsellSection.hidden = true;
      }
    } catch (err) {
      console.error('Recommendations error:', err);
      this.upsellSection.hidden = true;
    } finally {
      this.isLoadingRecs = false;
    }
  }

  // ══════════════════════════════════════════
  // EVENTS OUT
  // ══════════════════════════════════════════

  dispatchCartUpdate(cart) {
    document.body.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
  }

  // ══════════════════════════════════════════
  // UTILITIES
  // ══════════════════════════════════════════

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // EUR — replace . with , for European formatting
    return `€${amount.replace('.', ',').replace(',00', ',-')}`;
  }

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
  }

  escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('cart-drawer', CartDrawer);
